/**
 * <blog-post-toc> — the sticky "Cuprins" list on an article page (sections/blog-post.liquid).
 *
 * The article body is written in the Shopify editor, so the headings only exist in the rendered
 * HTML. This element reads them, gives each one an id, fills its own <ol> with links and keeps the
 * heading the reader is at marked as current. With fewer than two headings it stays hidden, which
 * is also the state without JavaScript.
 *
 * Markup contract:
 *   <blog-post-toc data-content="<selector of the article body>" hidden>
 *     <p class="bp-toc__title">…</p>
 *     <ol class="bp-toc__list"></ol>
 *   </blog-post-toc>
 *
 * Above 990px the page scrolls inside `.page-wrapper`, not the window, so the scroll listener goes
 * on whichever of the two actually scrolls.
 */
const ACTIVE_CLASS = 'bp-toc__item--active';
const ACTIVE_OFFSET = 140;

class BlogPostToc extends HTMLElement {
  /** @type {HTMLElement[]} */
  #headings = [];
  /** @type {HTMLLIElement[]} */
  #items = [];
  /** @type {HTMLElement | null} */
  #scroller = null;
  #ticking = false;
  #activeIndex = -1;

  connectedCallback() {
    const content = document.querySelector(this.dataset.content || '');
    const list = this.querySelector('ol');
    if (!content || !list) return;

    this.#headings = [...content.querySelectorAll('h2')].filter((heading) => heading.textContent?.trim());
    if (this.#headings.length < 2) return;

    const fragment = document.createDocumentFragment();
    this.#headings.forEach((heading, index) => {
      if (!heading.id) heading.id = `article-section-${index + 1}`;

      const item = document.createElement('li');
      item.className = 'bp-toc__item';

      const link = document.createElement('a');
      link.className = 'bp-toc__link';
      link.href = `#${heading.id}`;
      link.textContent = heading.textContent?.trim() ?? '';

      item.append(link);
      fragment.append(item);
      this.#items.push(item);
    });

    list.replaceChildren(fragment);
    this.hidden = false;

    // Above 990px the page scrolls inside `.page-wrapper`; below it the window does.
    this.#scroller = this.closest('.page-wrapper');
    this.#scroller?.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('resize', this.#onScroll, { passive: true });
    this.#update();
  }

  disconnectedCallback() {
    this.#scroller?.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('resize', this.#onScroll);
  }

  #onScroll = () => {
    if (this.#ticking) return;
    this.#ticking = true;
    requestAnimationFrame(() => {
      this.#ticking = false;
      this.#update();
    });
  };

  /** The current section is the last heading whose top has passed the offset. */
  #update() {
    let index = 0;
    this.#headings.forEach((heading, i) => {
      if (heading.getBoundingClientRect().top <= ACTIVE_OFFSET) index = i;
    });

    if (index === this.#activeIndex) return;
    this.#activeIndex = index;

    this.#items.forEach((item, i) => {
      item.classList.toggle(ACTIVE_CLASS, i === index);
      const link = item.firstElementChild;
      if (link) {
        if (i === index) {
          link.setAttribute('aria-current', 'true');
        } else {
          link.removeAttribute('aria-current');
        }
      }
    });
  }
}

if (!customElements.get('blog-post-toc')) {
  customElements.define('blog-post-toc', BlogPostToc);
}
